import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Box, Typography, Paper, Divider, Stack, TextField, Autocomplete, IconButton, MenuItem } from '@mui/material';
import cambioEstadoService from '../../services/cambioEstadoService';
import { obtenerSiguienteEstadoART } from '../../services/art/artEstadoService';
import { crearSiniestro } from '../../services/art/siniestroService';
import { getLesiones } from '../../services/art/lesionService';
import { subirArchivo, descargarArchivo, eliminarArchivo, obtenerArchivosPorTramite } from '../../services/archivoService';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const TIPOS_CONTINGENCIA_TELEGRAMA = [
    { code: '', label: 'Sin especificar' },
    { code: 'AT', label: 'Accidente de Trabajo' },
    { code: 'EP', label: 'Enfermedad Profesional' },
    { code: 'EPNL', label: 'Enfermedad Profesional No Listada' },
];

// El catálogo de lesiones del backend usa `idLesion`. Normalizamos por si algún
// día llega con otra clave, pero ya no asumimos `idLesionFisica`.
const obtenerIdLesion = (l) => l?.idLesion ?? l?.id;

function GestorTelegramaEnviado({ tramite, onAvanzar }) {
    const fileInputRef = useRef(null);

    const [cargando, setCargando] = useState(false);
    const [comentarioGlobal, setComentarioGlobal] = useState('');

    // Siniestros
    const [lesionesDisponibles, setLesionesDisponibles] = useState([]);
    const [siniestrosLocales, setSiniestrosLocales] = useState([]);

    // Archivos
    const [archivos, setArchivos] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [cargandoArchivos, setCargandoArchivos] = useState(true);

    useEffect(() => {
        cargarArchivos();
        cargarLesiones();
    }, []);

    const cargarLesiones = async () => {
        try {
            const data = await getLesiones();
            console.log('Lesiones cargadas:', data); // DEBUG
            setLesionesDisponibles(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error cargando lesiones:", error);
            toast.error("No se pudieron cargar las lesiones.");
        }
    };

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                esDelBackend: true
            }));
            setArchivos(archivosMapeados);
        } catch (error) {
            console.error("Error cargando archivos:", error);
        } finally {
            setCargandoArchivos(false);
        }
    };

    // --- GESTIÓN DE ARCHIVOS ---
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (e) => {
        const archivosSeleccionados = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            archivo: file,
            estado: "pendiente",
            esDelBackend: false
        }));
        setArchivos(prev => [...prev, ...archivosSeleccionados]);
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) return;

        setSubiendoArchivos(true);
        try {
            for (const archivo of archivosPendientes) {
                try {
                    await subirArchivo(tramite.idTramite, archivo.archivo);
                    setArchivos(prev => prev.map(a => a.id === archivo.id ? { ...a, estado: 'subido' } : a));
                } catch (error) {
                    console.error(`Error subiendo ${archivo.nombre}:`, error);
                    setArchivos(prev => prev.map(a => a.id === archivo.id ? { ...a, estado: 'error' } : a));
                }
            }
            toast.success('Archivos subidos correctamente');
        } catch (error) {
            console.error('Error general subiendo archivos:', error);
            toast.error('Error al subir algunos archivos');
        } finally {
            setSubiendoArchivos(false);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        try {
            await descargarArchivo(archivo.idArchivo, archivo.nombre);
        } catch (error) {
            toast.error('Error al descargar archivo');
        }
    };

    const handleEliminarArchivo = async (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (!archivo) return;

        if (archivo.esDelBackend && archivo.idArchivo) {
            try {
                await eliminarArchivo(archivo.idArchivo);
                setArchivos(prev => prev.filter(a => a.id !== id));
                toast.success('Archivo eliminado');
            } catch (error) {
                toast.error('Error al eliminar archivo');
            }
        } else {
            setArchivos(prev => prev.filter(a => a.id !== id));
        }
    };

    // --- GESTIÓN DE SINIESTROS ---
    const handleAgregarSiniestroFila = () => {
        setSiniestrosLocales(prev => [
            ...prev,
            {
                localId: Date.now(),
                numeroSiniestro: '',
                nroExpedienteSRT: '',
                tipoContingencia: '',
                lesionesSeleccionadas: [],
                comentario: ''
            }
        ]);
    };

    const handleEliminarSiniestro = (localId) => {
        setSiniestrosLocales(prev => prev.filter(s => s.localId !== localId));
    };

    const handleChangeSiniestro = (localId, field, value) => {
        setSiniestrosLocales(prev => prev.map(s => {
            if (s.localId === localId) {
                return { ...s, [field]: value };
            }
            return s;
        }));
    };

    const handleConfirmarEnvio = async () => {
        const archivosPendientes = archivos.some(a => a.estado === 'pendiente');
        if (archivosPendientes) {
            toast.error('Debe subir todos los archivos pendientes primero');
            return;
        }

        // Validar siniestros parciales
        for (let s of siniestrosLocales) {
            if (!s.numeroSiniestro || !s.lesionesSeleccionadas || s.lesionesSeleccionadas.length === 0) {
                toast.warning('Existen siniestros incompletos. Por favor complete Nro y al menos una Lesión para todos, o elimine los vacíos.');
                return;
            }
        }

        setCargando(true);
        try {
            // Guardar Siniestros
            for (let s of siniestrosLocales) {
                const reqSiniestro = {
                    nroSiniestro: s.numeroSiniestro,
                    nroExpedienteSRT: s.nroExpedienteSRT?.trim() ? s.nroExpedienteSRT.trim() : null,
                    tipoContingencia: s.tipoContingencia || null,
                    idsLesiones: s.lesionesSeleccionadas.map(obtenerIdLesion).filter(Boolean),
                    idTramite: tramite.idTramite,
                    comentario: s.comentario
                };
                console.log('Guardando siniestro:', reqSiniestro); // DEBUG
                await crearSiniestro(reqSiniestro);
            }

            // Cambiar Estado
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, 'Citación ART');
            let comentarioFinal = comentarioGlobal.trim() ? `Gestión Telegrama y Siniestros: ${comentarioGlobal}` : `Respuesta procesada.`;

            if (siniestrosLocales.length > 0) {
                comentarioFinal += `\nSe registraron ${siniestrosLocales.length} siniestro(s).`;
            }

            const reqEstado = {
                idEstado: siguienteEstado.id,
                comentario: comentarioFinal
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, reqEstado);

            toast.success("Trámite avanzado a Citación ART");
            onAvanzar();
        } catch (error) {
            console.error(error);
            toast.error("Error procesando los datos: " + error.message);
        } finally {
            setCargando(false);
        }
    };

    const pend = archivos.some(a => a.estado === 'pendiente');

    const bannerTelegrama = (() => {
        // Intentar parsear la fecha exacta desde el comentario del estado
        const match = (tramite?.ultimoComentario || '').match(/[Ff]echa\s+de\s+env\S*o:\s*(\d{4}-\d{2}-\d{2})/);
        let fechaEnvio = null;
        let fechaEsAproximada = false;

        if (match) {
            const d = new Date(match[1] + 'T00:00:00');
            if (!isNaN(d.getTime())) fechaEnvio = d;
        }

        // Fallback: usar fechaEstadoActual si no hay fecha registrada en el comentario
        if (!fechaEnvio && tramite?.fechaEstadoActual) {
            const d = new Date(tramite.fechaEstadoActual);
            if (!isNaN(d.getTime())) { fechaEnvio = d; fechaEsAproximada = true; }
        }

        if (!fechaEnvio) return null;

        fechaEnvio.setHours(0, 0, 0, 0);
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        const meses = Math.floor((hoy.getFullYear() - fechaEnvio.getFullYear()) * 12 + (hoy.getMonth() - fechaEnvio.getMonth()));
        const textoElapsed = meses === 0
            ? `Enviado este mes`
            : `Enviado hace ${meses} mes${meses !== 1 ? 'es' : ''}`;
        const scheme = meses >= 3
            ? { header: '#7f1d1d', borde: '#dc2626', badge: '#dc2626', sub: '#fca5a5', alerta: 'Requiere seguimiento urgente — sin respuesta de la ART' }
            : meses >= 1
                ? { header: '#78350f', borde: '#f59e0b', badge: '#f59e0b', sub: '#fcd34d', alerta: 'Pendiente de respuesta de la ART' }
                : { header: '#1e3a5f', borde: '#3b82f6', badge: '#3b82f6', sub: '#93c5fd', alerta: 'Esperando respuesta de la ART' };

        return (
            <Box sx={{
                background: scheme.header,
                border: `2px solid ${scheme.borde}`,
                borderRadius: 3, p: 3, mb: 3,
            }}>
                <Typography variant="overline" sx={{ color: scheme.sub, letterSpacing: 2, fontSize: '0.7rem', display: 'block' }}>
                    TELEGRAMA ENVIADO EL
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.3, mt: 0.5, textTransform: 'capitalize' }}>
                    {fechaEnvio.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </Typography>
                {fechaEsAproximada && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        (Fecha estimada — no se registró la fecha exacta de envío)
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                    <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: 2, px: 2, py: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                            <i className="bi bi-clock-history me-2"></i>{textoElapsed}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: scheme.sub, fontWeight: 500 }}>
                        {scheme.alerta}
                    </Typography>
                </Box>
            </Box>
        );
    })();

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            {bannerTelegrama}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Telegrama Enviado</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <BusinessCenterIcon fontSize="small" className="me-1" />
                    Generación de Siniestros
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Cargue la respuesta o constancia de la ART, y genere los siniestros (expedientes) correspondientes antes de avanzar a la Citación.</p>
            </div>

            <Stack spacing={4} sx={{ mt: 4 }}>
                {/* --- SECCIÓN MULTIPLES SINIESTROS --- */}
                <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                        Siniestros (Expedientes)
                    </Typography>

                    <Stack spacing={2}>
                        {siniestrosLocales.map((siniestro, index) => (
                            <Paper key={siniestro.localId} elevation={1} sx={{ p: 2, border: '1px solid #dee2e6', borderRadius: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">Siniestro #{index + 1}</Typography>
                                    <IconButton size="small" color="error" onClick={() => handleEliminarSiniestro(siniestro.localId)} title="Eliminar Siniestro">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                    <TextField
                                        label="Número de Siniestro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={siniestro.numeroSiniestro}
                                        onChange={(e) => handleChangeSiniestro(siniestro.localId, 'numeroSiniestro', e.target.value)}
                                        required
                                    />
                                    <Autocomplete
                                        multiple
                                        options={lesionesDisponibles}
                                        getOptionLabel={(o) => o.lesion || o.nombre || o.descripcion || ''}
                                        value={siniestro.lesionesSeleccionadas || []}
                                        onChange={(e, val) => handleChangeSiniestro(siniestro.localId, 'lesionesSeleccionadas', val || [])}
                                        isOptionEqualToValue={(o, v) => obtenerIdLesion(o) === obtenerIdLesion(v)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Lesiones"
                                                variant="outlined"
                                                size="small"
                                                required={(siniestro.lesionesSeleccionadas || []).length === 0}
                                            />
                                        )}
                                        noOptionsText="No se encontraron lesiones"
                                        loading={lesionesDisponibles.length === 0}
                                    />
                                </Box>

                                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                    <TextField
                                        label="Nro. Expediente SRT (opcional)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={siniestro.nroExpedienteSRT}
                                        onChange={(e) => handleChangeSiniestro(siniestro.localId, 'nroExpedienteSRT', e.target.value)}
                                    />
                                    <TextField
                                        select
                                        label="Tipo de Contingencia (opcional)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={siniestro.tipoContingencia}
                                        onChange={(e) => handleChangeSiniestro(siniestro.localId, 'tipoContingencia', e.target.value)}
                                    >
                                        {TIPOS_CONTINGENCIA_TELEGRAMA.map(t => (
                                            <MenuItem key={t.code || 'vacio'} value={t.code}>{t.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <TextField
                                    label="Observaciones (Opcional)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={siniestro.comentario}
                                    onChange={(e) => handleChangeSiniestro(siniestro.localId, 'comentario', e.target.value)}
                                />
                            </Paper>
                        ))}
                    </Stack>

                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAgregarSiniestroFila}
                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Agregar Nuevo Siniestro
                    </Button>
                </Box>

                <Divider />

                {/* --- GESTIÓN DE ARCHIVOS (Respuesta ART) --- */}
                <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                        Respuesta de la ART (Documentación)
                    </Typography>
                    {cargandoArchivos ? (
                        <Typography variant="body2" color="text.secondary">Cargando archivos existentes...</Typography>
                    ) : (
                        <>
                            {archivos.length > 0 && (
                                <div className="list-group mb-3">
                                    {archivos.map(archivo => (
                                        <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div>
                                                <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                                <span className="fw-semibold">{archivo.nombre}</span>
                                                <small className="text-muted ms-2">({archivo.tamaño})</small>
                                                <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : archivo.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                    {archivo.estado}
                                                </span>
                                            </div>
                                            <div>
                                                {archivo.esDelBackend && (
                                                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleDescargarArchivo(archivo)} title="Descargar">
                                                        <DownloadIcon fontSize="small" />
                                                    </button>
                                                )}
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(archivo.id)} title="Eliminar">
                                                    <DeleteIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div
                                className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current.click()}
                                style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#cfe2ff' }}
                            >
                                <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                                <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                                <p className="mb-0">Arrastra archivos aquí o haz clic para adjuntar la respuesta o comprobantes</p>
                            </div>

                            {pend && (
                                <div className="d-flex justify-content-center mt-3">
                                    <button className="btn btn-primary rounded-pill px-4" onClick={handleSubirArchivos} disabled={subiendoArchivos}>
                                        {subiendoArchivos ? 'Subiendo...' : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </Box>

                <Divider />

                {/* --- FEEDBACK / COMENTARIO GLOBAL --- */}
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={1}>
                        Comentario General del Estado
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        placeholder="Observaciones generales sobre la respuesta de la ART..."
                        value={comentarioGlobal}
                        onChange={(e) => setComentarioGlobal(e.target.value)}
                    />
                    <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        Este comentario cerrará el estado "Telegrama Enviado" en el historial.
                    </Typography>
                </Box>
            </Stack>

            {/* --- ACCIONES DE SALIDA --- */}
            <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={handleConfirmarEnvio}
                    disabled={cargando || subiendoArchivos || pend}
                    title={pend ? "Debe subir los archivos pendientes" : "Avanzar a Citación ART"}
                >
                    <AssignmentTurnedInIcon fontSize="small" />
                    Avanzar a Citación ART
                </button>
            </div>
        </div>
    );
}

export default GestorTelegramaEnviado;
